/**
 * Chart colour tokens — SightHost.
 *
 * DERIVED, NOT PICKED. Every value below is a brand hue re-stepped until it
 * passed all five computable checks against the portal's #ffffff card surface.
 *
 * The brand token set fails outright as a series palette. Fed in as-is:
 *
 *   $ validate_palette "#1e3a5f,#F6960C,#4A7C8C,#3182ce" --mode light
 *     [FAIL] Lightness band       outside band: #1e3a5f (L 0.346)
 *     [FAIL] Chroma floor         reads gray: #1e3a5f (0.074), #4A7C8C (0.059)
 *     [FAIL] Normal-vision floor  #3182ce↔#4A7C8C  ΔE 10.0
 *     [WARN] Contrast vs surface  #F6960C at 2.26:1
 *
 * — the old navy was too dark and too grey to carry identity, the brand teal reads as
 * grey, teal and trust-blue collapse into each other, and the brand orange sits
 * below the 3:1 floor for a mark. So the slots below hold the same hue families
 * at different lightness and chroma.
 *
 * ─── THE VALIDATED RESULT, committed so the next person to change a value
 *     knows what it has to clear ─────────────────────────────────────────────
 *
 *   $ validate_palette "#2470bd,#c98708,#0891b2" --mode light
 *     [PASS] Lightness band       all 3 inside L 0.43–0.77
 *     [PASS] Chroma floor         all 3 >= 0.1
 *     [PASS] CVD separation       worst adjacent  ΔE 19.2 (protan)
 *     [PASS] Normal-vision floor  worst adjacent  ΔE 25.1
 *     [PASS] Contrast vs surface  all 3 >= 3:1
 *     → ALL CHECKS PASS
 *
 *   With `violet` appended it still PASSES on adjacent pairs (worst ΔE 8.0
 *   simulated, 17.0 normal) and FAILS on all-pairs:
 *
 *     $ validate_palette "…,#7c5cbf" --mode light --pairs all
 *       [FAIL] CVD separation       #7c5cbf↔#2470bd  ΔE  2.0 (deutan)
 *       [FAIL] Normal-vision floor  #0891b2↔#2470bd  ΔE 10.0
 *
 *   That difference is the whole reason this file ships no pie or donut form.
 *   In a stacked bar only NEIGHBOURS touch, so each colour must be told apart
 *   from two others. In a pie every slice meets every other around the centre,
 *   so each must be told apart from ALL of them. Identical data, identical
 *   colours, and only one of the two is readable. Use ChartStack.
 *
 * LIGHT MODE ONLY. The portal declares no dark theme — portal/src/globals.css
 * has no `.dark` block. If one ever ships, RE-STEP these against the dark
 * surface. Do not invert them: an inverted ramp is not a validated ramp.
 */

export const chartColors = {
  /**
   * Series identity. Assign in order, never cycled — the order IS the
   * CVD-safety mechanism. Capped at 4; a 5th series folds into "Other",
   * facets into small multiples, or the chart form changes. A generated 5th
   * hue is indistinguishable from an existing one under simulation by
   * construction, so it is never the answer.
   */
  categorical: [
    '#2470bd', // 1 · blue   — already --color-trust-dark. The default single-series hue.
    '#c98708', // 2 · ochre  — the logo's orange hue, darkened until it clears 3:1 as a fill.
    '#0891b2', // 3 · cyan   — the brand teal, re-saturated to clear the chroma floor.
    '#7c5cbf', // 4 · violet — NEIGHBOUR-SAFE ONLY. Legal in a stack, never where all marks meet.
  ],

  /**
   * Ordered data — pipeline stages, size tiers, lifecycle buckets, age bands.
   * One hue in steps, light → dark, so the reader sees the sequence in the
   * colour itself. If reordering the categories would change the meaning, it
   * belongs here and not in `categorical`.
   *
   *   $ validate_palette "#8FC3D3,#4AA3BE,#1B4C5B" --ordinal --mode light
   *     [PASS] Lightness monotone · Adjacent ΔL · Light-end contrast 2.14:1 · Single hue (4°)
   */
  ordinal: [
    '#8FC3D3', // light step, 2.05:1 on white
    '#4AA3BE', // = brandColors.secondaryOnDark
    '#1B4C5B', // brand teal, one step deeper
  ],

  /** Recessive chart furniture. Never a series. */
  grid: '#eef1f6',
  axisLabel: '#9aa6b8',

  /** The surface every value above was validated against. */
  surface: '#ffffff',
} as const;

/**
 * NOT a series slot, deliberately.
 *
 * brandColors.primary (#F58C1E) is 2.43:1 on white — below the 3:1 floor for a
 * mark, and the same ratio that already bars white text on it (CLAUDE.md §4.2).
 * It stays what it is: the accent, on buttons and rules. `categorical[1]`
 * (#c98708) is its chart-safe sibling and holds the same hue.
 *
 * Status colours are also absent on purpose. success / warning / danger have
 * reserved meanings, and on the Sales dashboard amber genuinely means
 * "quotation expiring within 7 days" — a series wearing it would be a second
 * meaning for one hue on one screen. When a series genuinely MEANS good or bad
 * (pass/fail, error rate) it wears the status token and never a categorical
 * one; it is never both in one chart.
 */

export type ChartCategoricalSlot = (typeof chartColors.categorical)[number];
export type ChartOrdinalStep = (typeof chartColors.ordinal)[number];
